/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { Grid } from '#UNSAFE_Grid';
import { TestIdProps } from '#hooks/UNSAFE_useTestId';
import { getClosestDecade } from '#utils/UNSAFE_calendarDateUtils';
import { ComponentProps } from 'preact/compat';
import { range } from 'ramda';
import { YearCell } from './YearCell';
import { BundleType } from '#resources/nls/bundle';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';

/**
 * Represents the total number of years rendered in the view
 */
const NUM_YEARS_IN_GRID = 11;

type PickedYearCellProps = Pick<ComponentProps<typeof YearCell>, 'onAction' | 'onNavigation'>;
type Props = PickedYearCellProps &
  TestIdProps & {
    /**
     * Specifies the current year determined from the today's date.
     */
    currentYear?: number;

    /**
     * Specifies which year cell should be focused first when tabbing
     * into the YearsGridView.
     */
    focusableYear: number;

    /**
     * Specifies if the focusableYear year cell is currently focused.
     * @default false
     */
    isFocused?: boolean;

    /**
     * Specifies which year is currently selected.
     */
    selectedYear?: number;
  };

/**
 * This component renders the Years Grid View for the DatePicker component.
 */
export const YearsGridView = ({
  currentYear,
  focusableYear,
  isFocused = false,
  selectedYear,
  testId,
  onAction,
  onNavigation
}: Props) => {
  const decade = getClosestDecade(focusableYear);
  // Lay out the 11 Years in a 3-row grid.
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const yearsAriaLabel = translations.datePicker_years();

  return (
    <Grid role="grid" gap={['13.5x', '0x']} testId={testId} aria-label={yearsAriaLabel}>
      {[0, 1, 2].map((rowIndex) => (
        <Grid key={rowIndex} role="row" gap={['0x', '3x']} gridTemplateColumns="repeat(4, 1fr)">
          {range(rowIndex * 4, rowIndex * 4 + 4).map((index) => {
            const year = decade + index;
            if (year < decade + NUM_YEARS_IN_GRID) {
              return (
                <div role="gridcell">
                  <YearCell
                    key={`${year}`}
                    year={year}
                    isCurrent={year === currentYear}
                    isFocusable={year === focusableYear}
                    isFocused={year === focusableYear && isFocused}
                    isSelected={year === selectedYear}
                    onAction={onAction}
                    onNavigation={onNavigation}
                  />
                </div>
              );
            }
            return null;
          })}
        </Grid>
      ))}
    </Grid>
  );
};
